import BulkEditShots from '@/connection/greenroom/bulk-edit-shots';
import GetGreenroomInvitationsForMeeting from '@/connection/greenroom/get-greenroom-invitations-for-meeting';
import LoadGreenroomShots from '@/connection/greenroom/load-greenroom-shots';
import PostToGreenroomSocket from '@/connection/greenroom/post-to-greenroom-socket';
import { GreenroomSessionMeetingData } from '@/store/slices/project/types';
import { GreenroomInviteUserResponse } from '@/types/greenroom/profile.types';
import { baseShotFeed, FEED_TYPES, Shot, ShotFeed } from '@/types/greenroom/shots.types';
import { Speaker } from '@/types/speakers.types';
import toastTrigger from '@ui/toast/toast-trigger';

const syncRemovePresenterFromGreenroomFeeds = async ({
  sessionMeetingData,
  speakerToRemove,
}: {
  sessionMeetingData: GreenroomSessionMeetingData | null;
  speakerToRemove: Speaker | null;
}) => {
  if (!sessionMeetingData?.meeting?.meeting) return;

  // Strip feed from shots
  const matchedInvite = sessionMeetingData?.invites?.find(
    (i) => i.profile?.events_profile === speakerToRemove?.bl_admin?.id
  );
  const grUser = matchedInvite?.profile?.gr_user;
  const presenterEmail = matchedInvite?.profile?.email || speakerToRemove?.email;
  if (matchedInvite && grUser && presenterEmail) {
    try {
      // fetch latest shots, definitely don't want to update with stale shots
      const latestShots = await LoadGreenroomShots(sessionMeetingData.meeting.meeting);
      if (latestShots) {
        const resetFeed = (
          inputId: number | string | null,
          inputType: string,
          feed: ShotFeed,
          altId: number | string | null
        ): ShotFeed => {
          let id = inputId;
          let type = inputType;

          // we also want to reset any corresponding screenshare feeds for presenters
          if (feed.feedType === FEED_TYPES.SCREENSHARE && type === FEED_TYPES.PRESENTER) {
            id = altId;
            type = FEED_TYPES.SCREENSHARE;
          }

          const typeMatch = feed.feedType === type;
          const idMatch =
            (type === FEED_TYPES.MEDIA_AUDIO && feed.media === id) ||
            (type === FEED_TYPES.MEDIA && feed.media === id) ||
            (type === FEED_TYPES.PRESENTER && feed.presenter === id) ||
            (type === FEED_TYPES.SCREENSHARE && feed.screenshare === id) ||
            (type === FEED_TYPES.SIP_CALL && feed.sipStreamId === id) ||
            (type === FEED_TYPES.SRT_STREAM && feed.srtStreamId === id) ||
            (type === FEED_TYPES.ONSITE_LOCATION && feed.onsiteLocationId === id);
          if (typeMatch && idMatch) {
            return {
              ...baseShotFeed,
              position: feed.position,
              audioOnly: !!feed.audioOnly,
            };
          }
          return feed;
        };
        const editedShots = latestShots.reduce((acc: Shot[], shot: Shot) => {
          let wasEdited = false;
          const newShot: Shot = {
            ...shot,
            shot_data: {
              ...shot.shot_data,
              shotFeeds: shot.shot_data.shotFeeds.map((feed) => {
                const newFeed = resetFeed(grUser, FEED_TYPES.PRESENTER, feed, presenterEmail);
                if (feed.feedType !== newFeed.feedType) wasEdited = true;
                return newFeed;
              }),
            },
          };
          if (wasEdited) acc.push(newShot);
          return acc;
        }, []);

        if (editedShots.length > 0) {
          try {
            await BulkEditShots({ shots: editedShots });
          } catch (error) {
            console.error(error);
          }
        }

        // always fetch latest invites before sending out, don't want to send stale data to GR.
        // This should not contain the current invitee because we revoked their invitation above.
        const latestInvitations = await GetGreenroomInvitationsForMeeting({
          meeting: sessionMeetingData.meeting.meeting,
        });
        if (latestInvitations) {
          await PostToGreenroomSocket<GreenroomInviteUserResponse[]>({
            channel: `meeting-${sessionMeetingData.meeting.meeting}`,
            type: 'invites-update',
            message: latestInvitations,
          });
        }
      }
    } catch (error) {
      console.error(error);
      toastTrigger(
        `
					<div class="S1">
						<div>There was an error syncing Greenroom® shots</div>
						<div>If Greenroom is currently live, shot feeds may still contain the presenter.</div>
						<div>You will need to enter Greenroom® and adjust manually.</div>
					</div>
				`,
        {
          type: 'error',
          autoClose: 10000,
        }
      );
    }
  }
};

export default syncRemovePresenterFromGreenroomFeeds;
